import { useEffect, useMemo } from 'react';
import {
    Container,
    Stack,
    Paper,
    Box,
    Chip,
    Grid
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import dayjs from 'dayjs';

import Back from '../../../components/common/Back.component';
import Loading from '../../../components/common/Loading.component';
import TemplateDelete from '../../../pages/templates/Delete.template';
import LastUpdateInfo from '../../../components/lastUpdateInfo/LastUpdateInfo.component';
import DetailView from '../../../views/Detail.view';
import Section from '../../../components/detail/components/Section.component';
import Text from '../../../components/detail/components/Text.component';
import handover from '../../../module/handover';

const HandoverDetailPage = ({ module }) => {
    const { tag, detail, query } = module;
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { jwt } = useSelector((state) => state.login);
    const userAuth = useSelector((state) => state.login?.user);
    const { loading, data, error } = useSelector(detail.selector.data);

    useEffect(() => {
        if (userAuth?.permissions?.[_.toUpper(tag)]?.READ === 'true') {
            dispatch(
                query.detail.action({
                    settings: query.detail.settings,
                    token: jwt,
                    params: { id },
                    query: { populate: query.detail.populate },
                })
            );
        }

        return () => {
            dispatch(
                query.detail.action({
                    settings: null,
                    token: null,
                    params: null,
                    query: null,
                })
            );
        };
        // eslint-disable-next-line
    }, [id, userAuth, jwt]);

    useEffect(() => {
        if (
            error &&
            (error?.response?.status === 404 || error?.response?.status === 500)
        ) {
            navigate('/404');
        }
    }, [error, navigate]);

    const attributes = data || {};
    const notes = attributes?.notes || [];
    const updatedAt = attributes?.updatedAt;
    const isClosed = attributes?.status === 'CLOSED';

    const detailConf = useMemo(() => {
        if (isClosed) {
            return detail.conf;
        } else {
            return {
                ...detail.conf,
                fields: detail.conf.fields.map((section) => ({
                    ...section,
                    items: section.items.filter(
                        (item) => item.key !== 'attributes.closed_by_user'
                    ),
                })),
            };
        }

    }, [detail.conf, isClosed]);

    return (
        <Container disableGutters maxWidth={false} sx={{ height: '100%' }}>
            <Stack spacing={2} justifyContent='center' alignItems='center' width={1}>
                <Back title={tag} />

                {loading && <Loading />}

                <Paper variant='outlined' sx={{ p: 2, width: '100%' }}>
                    <Stack
                        mb={2}
                        direction='row-reverse'
                        justifyContent='space-between'
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent='flex-end'
                            spacing={1}
                        >

                            {userAuth?.permissions?.[_.toUpper(tag)]?.DELETE === 'true' && (
                                <TemplateDelete module={handover} />
                            )}
                        </Stack>

                        {detail?.viewID && id && (
                            <Box mb={2}>
                                <Chip label={`ID: ${id}`} color='primary' variant='outlined' />
                            </Box>
                        )}
                    </Stack>

                    {userAuth?.permissions?.[_.toUpper(tag)]?.READ === 'true' && (
                        <Stack spacing={3} width='100%'>
                            <Box width='100%'>
                                <DetailView data={{ attributes: data }} conf={detailConf} />
                            </Box>

                            {notes.length > 0 && (
                                <Section label={`Notes (${notes.length})`}>
                                    <Grid container ml={0.5}>
                                        {notes.map((note, index) => (
                                            <Grid item xs={12} key={index}>
                                                <Box
                                                    sx={{
                                                        pb: 2,
                                                        pt: 1,
                                                        pr: 1,
                                                        borderBottom: index < notes.length - 1 ? '1px solid' : 'none',
                                                        borderColor: 'divider',
                                                    }}
                                                >
                                                    <Text label='Comment' value={note.comment || '-'} />

                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            flexWrap: 'wrap',
                                                            mt: 1,
                                                        }}
                                                    >
                                                        <Chip
                                                            label={
                                                                note.created_at_note
                                                                    ? dayjs(note.created_at_note).format('DD/MM/YYYY HH:mm')
                                                                    : '-'
                                                            }
                                                            size='small'
                                                            variant='outlined'
                                                        />
                                                        <Chip
                                                            label={note.user_name || 'Unknown user'}
                                                            size='small'
                                                            color='primary'
                                                            variant='outlined'
                                                        />
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Section>
                            )}
                        </Stack>
                    )}

                    {updatedAt && (
                        <LastUpdateInfo
                            updatedAt={updatedAt}
                            username={
                                attributes?.id_user_last_updated?.data?.attributes
                                    ?.id_users_permissions_user?.data?.attributes?.username
                            }
                        />
                    )}
                </Paper>
            </Stack>
        </Container>
    );
};

export default HandoverDetailPage;